import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Form, Select, Input, Upload, message } from "antd";
import type { UploadFile } from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useProjects } from "../../../queries/projects";
import { useCreateTicket } from "../../../queries/tickets";
import { ProjectProps, Severity, Priority } from "../../../types/interfaces";
import { ApiError } from "../../../hooks/apiRequest";
import { SEVERITY_OPTIONS, PRIORITY_OPTIONS } from "../../../utils/levels";
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_HINT,
  MAX_TICKET_ATTACHMENTS,
  validateAttachment,
} from "../../../utils/attachments";

interface Props {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  projectId: string;
  subject: string;
  severity: Severity;
  priority: Priority;
}

/** Raise a ticket without leaving the page: project, multi-line description,
 *  severity, priority and up to 5 attachments (PDF, Word, PNG/JPG). */
export const NewTicketModal = ({ open, onClose }: Props) => {
  const navigate = useNavigate();
  const { data: projects } = useProjects({ enabled: open });
  const createTicket = useCreateTicket();
  const [form] = Form.useForm<FormValues>();
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const close = () => {
    form.resetFields();
    setFileList([]);
    onClose();
  };

  const handleSubmit = async () => {
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return; // inline validation messages are already shown
    }
    try {
      const ticket = await createTicket.mutateAsync({
        ...values,
        subject: values.subject.trim(),
        files: fileList.map((f) => f.originFileObj as File).filter(Boolean),
      });
      message.success("Ticket created");
      close();
      navigate(`/support/tickets/${ticket._id}`);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : "Could not create the ticket");
    }
  };

  return (
    <Modal
      title="Raise a ticket"
      open={open}
      onCancel={close}
      onOk={handleSubmit}
      okText="Create ticket"
      confirmLoading={createTicket.isPending}
      destroyOnClose
      width={560}
    >
      <p style={{ color: "var(--osi-text-muted)", marginTop: 0 }}>
        Pick the project this concerns, set how severe it is, and describe what's wrong or what you need.
      </p>
      <Form form={form} layout="vertical" initialValues={{ severity: "minor", priority: "medium" }}>
        <Form.Item name="projectId" label="Project" rules={[{ required: true, message: "Pick a project" }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Pick a project"
            options={(projects || []).map((p: ProjectProps) => ({ label: p.name, value: p._id }))}
            onChange={(id) => {
              const project = (projects || []).find((p: ProjectProps) => p._id === id);
              if (project) form.setFieldValue("severity", project.severity);
            }}
          />
        </Form.Item>
        <Form.Item name="subject" label="Subject" rules={[{ required: true, whitespace: true, message: "Describe the request" }]}>
          <Input.TextArea
            autoSize={{ minRows: 4, maxRows: 10 }}
            placeholder="What's wrong, or what do you need? Add as much detail as helps."
            maxLength={5000}
            showCount
          />
        </Form.Item>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Form.Item
            name="severity"
            label="Severity"
            tooltip="Business impact: how badly is work affected?"
            rules={[{ required: true }]}
          >
            <Select options={SEVERITY_OPTIONS} />
          </Form.Item>
          <Form.Item
            name="priority"
            label="Priority"
            tooltip="Urgency: how soon does this need attention?"
            rules={[{ required: true }]}
          >
            <Select options={PRIORITY_OPTIONS} />
          </Form.Item>
        </div>
        <Form.Item label="Attachments" extra={`${ATTACHMENT_HINT} · up to ${MAX_TICKET_ATTACHMENTS} files`} style={{ marginBottom: 0 }}>
          <Upload.Dragger
            multiple
            accept={ATTACHMENT_ACCEPT}
            fileList={fileList}
            maxCount={MAX_TICKET_ATTACHMENTS}
            beforeUpload={(file, newFiles) => {
              const problem = validateAttachment(file);
              if (problem) {
                message.error(problem);
                return Upload.LIST_IGNORE;
              }
              if (fileList.length + newFiles.indexOf(file) >= MAX_TICKET_ATTACHMENTS) {
                message.error(`You can attach up to ${MAX_TICKET_ATTACHMENTS} files`);
                return Upload.LIST_IGNORE;
              }
              return false;
            }}
            onChange={({ fileList: next }) => setFileList(next.filter((f) => f.originFileObj || f.status === "done"))}
            onRemove={(file) => {
              setFileList((list) => list.filter((f) => f.uid !== file.uid));
              return true;
            }}
          >
            <p className="ant-upload-drag-icon"><InboxOutlined /></p>
            <p className="ant-upload-text">Drop files here or click to browse</p>
          </Upload.Dragger>
        </Form.Item>
      </Form>
    </Modal>
  );
};
